'use client';

// ===================================================
// GAM Command Center — Floating Toolbar V3
// Redesigned: Lucide icons, below-selection positioning,
// button groups, alignment, gibberish convert, expanded menu
// ===================================================

import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Type,
  Highlighter,
  Link,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Languages,
  MoreHorizontal,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  CheckSquare,
  Quote,
  Code,
  Minus,
  Plus,
  RemoveFormatting,
  Copy,
  Trash2,
  Info,
  AlertTriangle,
  Lightbulb,
  CircleX,
  StickyNote,
  CaseSensitive,
  Paintbrush,
  Sparkles,
  Loader2,
  ListTodo,
  GripVertical,
} from 'lucide-react';
import { ColorMenu } from './ColorMenu';
import { EDITOR_FONT_GROUPS } from './fonts';
import { EditorOverlay, EDITOR_OVERLAY_Z } from './editor-overlay';
import type { AiTextActionKind, FloatingToolbarProps } from './types';
// Type-only imports to pull in module augmentations for ChainedCommands
import type {} from '@tiptap/extension-bold';
import type {} from '@tiptap/extension-italic';
import type {} from '@tiptap/extension-strike';
import type {} from '@tiptap/extension-underline';
import type {} from '@tiptap/extension-link';
import type {} from '@tiptap/extension-text-align';
import type {} from '@tiptap/extension-code-block';
// Pulls in the setCallout augmentation on ChainedCommands.
import type {} from './extensions/CalloutBlock';
// setFontFamily / unsetFontFamily live in the text-style package.
import type {} from '@tiptap/extension-text-style';

const ICON_SIZE = 15;

/**
 * Font sizes offered in the size menu. Values are written straight into the
 * textStyle mark as CSS, so they must carry their unit.
 */
const FONT_SIZES = [
  { label: 'קטן מאוד', value: '12px' },
  { label: 'קטן', value: '14px' },
  { label: 'רגיל', value: '16px' },
  { label: 'גדול', value: '18px' },
  { label: 'גדול מאוד', value: '24px' },
  { label: 'ענק', value: '32px' },
] as const;

/** Bounds and defaults for the numeric size / spacing steppers. */
const FONT_PX_MIN = 8;
const FONT_PX_MAX = 96;
const LINE_HEIGHT_MIN = 0.8;
const LINE_HEIGHT_MAX = 4;
const LINE_HEIGHT_STEP = 0.1;
const DEFAULT_LINE_HEIGHT = 1.6;

/** The nine placements, laid out as they appear in the picker (RTL rows). */
const PLACEMENTS = [
  ['top', 'right'], ['top', 'center'], ['top', 'left'],
  ['middle', 'right'], ['middle', 'center'], ['middle', 'left'],
  ['bottom', 'right'], ['bottom', 'center'], ['bottom', 'left'],
] as const;

const HORIZONTAL_LABELS: Record<string, string> = {
  right: 'ימין',
  center: 'מרכז',
  left: 'שמאל',
};

const PLACEMENT_LABELS: Record<string, string> = {
  'top-right': 'ימין למעלה',
  'top-center': 'מרכז למעלה',
  'top-left': 'שמאל למעלה',
  'middle-right': 'ימין באמצע',
  'middle-center': 'מרכז',
  'middle-left': 'שמאל באמצע',
  'bottom-right': 'ימין למטה',
  'bottom-center': 'מרכז למטה',
  'bottom-left': 'שמאל למטה',
};

/** Line spacing, unitless so it scales with whatever font size is in force. */
const LINE_HEIGHTS = [
  { label: 'צפוף', value: '1.2' },
  { label: 'רגיל', value: '1.6' },
  { label: 'מרווח', value: '2' },
  { label: 'כפול', value: '2.5' },
] as const;

/** The AI menu. Order is the order the rows appear in. */
const AI_ACTIONS: Array<{ kind: AiTextActionKind; label: string }> = [
  { kind: 'shorten', label: 'קצר' },
  { kind: 'rewrite', label: 'שכתב' },
  { kind: 'translate', label: 'תרגם' },
  { kind: 'improve', label: 'שפר' },
];

/**
 * Marks the format painter carries. Deliberately a whitelist: a link or an
 * objectRef is a reference to a specific thing, not formatting, and painting
 * it onto unrelated text would invent a connection nobody asked for.
 */
const PAINTABLE_MARKS = ['bold', 'italic', 'underline', 'strike', 'code', 'textStyle', 'highlight'];

type PaintedMark = { name: string; attrs: Record<string, unknown> };

/**
 * The callout variants CalloutBlock accepts, in the order they appear in the
 * "הפוך ל..." menu. The emoji is what the node renders; the lucide glyph is
 * only the menu row's icon.
 */
const CALLOUTS = [
  { variant: 'info', icon: 'ℹ️', label: 'הודעה', Glyph: Info },
  { variant: 'warning', icon: '⚠️', label: 'אזהרה', Glyph: AlertTriangle },
  { variant: 'tip', icon: '💡', label: 'טיפ', Glyph: Lightbulb },
  { variant: 'error', icon: '🚫', label: 'שגיאה', Glyph: CircleX },
  { variant: 'note', icon: '📝', label: 'הערה', Glyph: StickyNote },
] as const;

/**
 * A "family" is a fixed cluster of toolbar items (e.g. bold/italic/underline/
 * strike). DnD reordering (Gal, 22.09) is deliberately two-tier and never
 * crosses this boundary: a whole family moves as a block among the other
 * families, or an item moves among its own family-mates — an item can never
 * jump into a different family. `itemIds` is the family's fixed membership;
 * only the ORDER of families and the order-within-a-family are editable.
 */
interface ToolbarFamily {
  id: string;
  itemIds: string[];
}

interface FamilyOrderState {
  familyOrder: string[];
  itemOrder: Record<string, string[]>;
}

function reconcileFamilyOrder(saved: FamilyOrderState | null, families: ToolbarFamily[]): FamilyOrderState {
  const knownFamilyIds = families.map((f) => f.id);
  const familyOrder = saved
    ? [
        ...saved.familyOrder.filter((id) => knownFamilyIds.includes(id)),
        ...knownFamilyIds.filter((id) => !saved.familyOrder.includes(id)),
      ]
    : knownFamilyIds;
  const itemOrder: Record<string, string[]> = {};
  for (const fam of families) {
    const savedItems = saved?.itemOrder?.[fam.id] ?? [];
    itemOrder[fam.id] = [
      ...savedItems.filter((id) => fam.itemIds.includes(id)),
      ...fam.itemIds.filter((id) => !savedItems.includes(id)),
    ];
  }
  return { familyOrder, itemOrder };
}

/**
 * Persists the toolbar's family/item order to localStorage, per scope
 * ("main" row vs the "…" dropdown — each reorders independently). Client-only
 * (Rule 22 — this is a per-viewer convenience, not shared state that needs a
 * DB row): reordering never leaves the browser it was set in. A family or
 * item that no longer exists (a removed feature) is dropped on load; a new
 * one (a future vRule/vTag kind) is appended at the end of its family rather
 * than disappearing.
 */
function useFamilyOrder(scope: string, families: ToolbarFamily[]) {
  const storageKey = `gam-ftb-order:${scope}`;
  const familiesRef = useRef(families);
  familiesRef.current = families;

  const [state, setState] = useState<FamilyOrderState>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
      const parsed = raw ? (JSON.parse(raw) as FamilyOrderState) : null;
      return reconcileFamilyOrder(parsed, families);
    } catch {
      return reconcileFamilyOrder(null, families);
    }
  });

  // The family/item SET can change between renders (an optional family like
  // "ai" or "records" appears/disappears with its prop), so the saved order
  // is re-reconciled whenever the live shape changes — not just once on
  // mount, or a freshly-appeared family would never enter the saved list.
  useEffect(() => {
    setState((prev) => reconcileFamilyOrder(prev, familiesRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [families.map((f) => `${f.id}:${f.itemIds.join(',')}`).join('|')]);

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // Private mode / quota — the custom order just won't survive a reload.
    }
  }, [state, storageKey]);

  const moveFamily = useCallback((dragId: string, overId: string) => {
    if (dragId === overId) return;
    setState((prev) => {
      const order = prev.familyOrder.filter((id) => id !== dragId);
      const overIdx = order.indexOf(overId);
      order.splice(overIdx < 0 ? order.length : overIdx, 0, dragId);
      return { ...prev, familyOrder: order };
    });
  }, []);

  const moveItem = useCallback((familyId: string, dragId: string, overId: string) => {
    if (dragId === overId) return;
    setState((prev) => {
      const list = (prev.itemOrder[familyId] ?? []).filter((id) => id !== dragId);
      const overIdx = list.indexOf(overId);
      list.splice(overIdx < 0 ? list.length : overIdx, 0, dragId);
      return { ...prev, itemOrder: { ...prev.itemOrder, [familyId]: list } };
    });
  }, []);

  return { familyOrder: state.familyOrder, itemOrder: state.itemOrder, moveFamily, moveItem };
}

export function FloatingToolbar({ editor, gibberishConverter, aiTextAction, onCreateFromText }: FloatingToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const toolbarObserverRef = useRef<ResizeObserver | null>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [flipAbove, setFlipAbove] = useState(false);
  const [colorMenu, setColorMenu] = useState<'text' | 'background' | null>(null);
  const [showAlign, setShowAlign] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showFonts, setShowFonts] = useState(false);
  const [showSizes, setShowSizes] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [taskBusy, setTaskBusy] = useState(false);
  const [taskMsg, setTaskMsg] = useState<string | null>(null);
  // "הפוך למשימה (vTask)" opens a small kind-picker instead of creating
  // directly — structure for the future kinds from Gal's Q1 answer
  // (22.09: "הכנה לכל — vRule / vTag / vCollection / Reference"). Only vTask
  // is wired today; the rest render disabled ("בקרוב") so the shape exists
  // without pretending they work.
  const [taskKindOpen, setTaskKindOpen] = useState(false);
  // The format painter is intentionally NOT a dropdown: it stays armed across
  // the selection change that happens between copying and applying.
  const [painted, setPainted] = useState<PaintedMark[] | null>(null);

  // ─── DnD reordering (Gal, 22.09) ───────────────────────────────────────
  // "משפחה זזה / ערכים זזים בתוך משפחה בלבד": a whole family (a cluster of
  // related buttons) can move among the other families, and an item can move
  // among its own family-mates — never across families. `editMode` reveals
  // the grip handles on both the main row and the "…" dropdown; both persist
  // independently (see useFamilyOrder) so reordering one never touches the
  // other.
  const [editMode, setEditMode] = useState(false);
  const dragRef = useRef<{ scope: 'family' | 'item'; scopeKey?: string; id: string } | null>(null);

  const mainFamilies = useMemo<ToolbarFamily[]>(() => {
    const families: ToolbarFamily[] = [
      { id: 'format', itemIds: ['bold', 'italic', 'underline', 'strike'] },
      { id: 'color', itemIds: ['textColor', 'highlight', 'font', 'size'] },
      { id: 'link', itemIds: ['link'] },
      { id: 'align', itemIds: ['align'] },
      { id: 'painter', itemIds: ['painter'] },
    ];
    if (aiTextAction) families.push({ id: 'ai', itemIds: ['ai'] });
    if (gibberishConverter) families.push({ id: 'lang', itemIds: ['lang'] });
    return families;
  }, [aiTextAction, gibberishConverter]);

  const moreFamilies = useMemo<ToolbarFamily[]>(() => {
    const families: ToolbarFamily[] = [
      { id: 'headings', itemIds: ['h1', 'h2', 'h3'] },
      { id: 'lists', itemIds: ['bulletList', 'orderedList', 'taskList'] },
      { id: 'blocks', itemIds: ['quote', 'code', 'divider'] },
    ];
    if (onCreateFromText) families.push({ id: 'records', itemIds: ['taskCreator'] });
    families.push({ id: 'callouts', itemIds: CALLOUTS.map((c) => `callout-${c.variant}`) });
    families.push({ id: 'actions', itemIds: ['clearFormat', 'copyBlock', 'deleteBlock'] });
    return families;
  }, [onCreateFromText]);

  const mainOrder = useFamilyOrder('main', mainFamilies);
  const moreOrder = useFamilyOrder('more', moreFamilies);

  // Flex `order` values driven by the persisted state above. Family blocks
  // sit on multiples of 10 (leaving room for their trailing separator at
  // +1); items inside a family are 0..N and only matter relative to each
  // other, since the family wrapper is its own flex context.
  const famOrder = (order: FamilyOrderState, familyId: string) => {
    const idx = order.familyOrder.indexOf(familyId);
    return (idx < 0 ? order.familyOrder.length : idx) * 10;
  };
  const itemOrder = (order: FamilyOrderState, familyId: string, itemId: string) => {
    const idx = (order.itemOrder[familyId] ?? []).indexOf(itemId);
    return idx < 0 ? 999 : idx;
  };

  // Drag handlers are plain closures (not hooks) built fresh each render —
  // cheap for this few elements, and it keeps them reading the current
  // `editMode`/order-setter without an extra ref layer.
  const familyDragProps = (scopeKey: 'main' | 'more', familyId: string, moveFamily: (a: string, b: string) => void) =>
    !editMode
      ? {}
      : {
          draggable: true,
          onDragStart: () => { dragRef.current = { scope: 'family' as const, scopeKey, id: familyId }; },
          onDragOver: (e: React.DragEvent) => { e.preventDefault(); },
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            const drag = dragRef.current;
            if (drag?.scope === 'family' && drag.scopeKey === scopeKey && drag.id !== familyId) {
              moveFamily(drag.id, familyId);
            }
            dragRef.current = null;
          },
        };

  const itemDragProps = (scopeKey: 'main' | 'more', familyId: string, itemId: string, moveItem: (fam: string, a: string, b: string) => void) =>
    !editMode
      ? {}
      : {
          draggable: true,
          onDragStart: (e: React.DragEvent) => {
            e.stopPropagation();
            dragRef.current = { scope: 'item' as const, scopeKey: `${scopeKey}:${familyId}`, id: itemId };
          },
          onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); },
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const drag = dragRef.current;
            if (drag?.scope === 'item' && drag.scopeKey === `${scopeKey}:${familyId}` && drag.id !== itemId) {
              moveItem(familyId, drag.id, itemId);
            }
            dragRef.current = null;
          },
        };

  // Close all dropdowns
  const closeDropdowns = useCallback(() => {
    setColorMenu(null);
    setShowAlign(false);
    setShowMore(false);
    setShowFonts(false);
    setShowSizes(false);
    setShowAi(false);
  }, []);

  // Position toolbar below selection (flip above if near bottom)
  const updatePosition = useCallback(() => {
    const { selection } = editor.state;
    const { from, to, empty } = selection;

    if (empty) {
      setVisible(false);
      closeDropdowns();
      return;
    }

    try {
      const start = editor.view.coordsAtPos(from);
      const end = editor.view.coordsAtPos(to);
      const editorRect = editor.view.dom.getBoundingClientRect();

      // Viewport coordinates, not editor-relative ones: the toolbar is
      // rendered through a portal on document.body (see EditorOverlay), so
      // it is positioned against the viewport and not against the editor.
      const midX = (start.left + end.right) / 2;
      const belowTop = end.bottom + 8;
      const aboveTop = start.top - 48;

      // Flip above when the toolbar would land past the bottom — measured
      // against the VIEWPORT, not against the editor. The editor is taller
      // than the window and scrolls inside the page, so its own bottom edge
      // is often far below the fold; deciding by it put the toolbar (and with
      // it every menu it opens) outside the visible area entirely.
      const toolbarHeight = 42; // approximate
      const viewportBottom = Math.min(window.innerHeight, editorRect.bottom);
      const fitsBelow = belowTop + toolbarHeight <= viewportBottom - 8;
      const fitsAbove = aboveTop >= 8;
      const shouldFlip = !fitsBelow && fitsAbove;

      // Last resort when the selection fits neither way (a selection taller
      // than the window): keep the toolbar on screen rather than beside a
      // point nobody can see.
      const rawTop = shouldFlip ? aboveTop : belowTop;
      const clampedTop = Math.min(
        Math.max(rawTop, 8),
        Math.max(8, window.innerHeight - toolbarHeight - 8),
      );

      setFlipAbove(shouldFlip);
      setPosition({
        top: clampedTop,
        left: midX,
      });
      setVisible(true);
    } catch {
      setVisible(false);
    }
  }, [editor, closeDropdowns]);

  useEffect(() => {
    editor.on('selectionUpdate', updatePosition);
    editor.on('transaction', updatePosition);

    return () => {
      editor.off('selectionUpdate', updatePosition);
      editor.off('transaction', updatePosition);
    };
  }, [editor, updatePosition]);

  // The toolbar is position:fixed while the selection scrolls with the page,
  // so scrolling or resizing has to re-run the same viewport arithmetic —
  // otherwise the toolbar stays put while the text it belongs to moves away.
  useEffect(() => {
    if (!visible) return;
    const onMove = () => updatePosition();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [visible, updatePosition]);

  // Hide on blur
  useEffect(() => {
    const handleBlur = () => {
      setTimeout(() => {
        // A number field being typed into counts as "still in use" just as
        // much as a hovering mouse does.
        const bar = toolbarRef.current;
        const typingInside = bar?.contains(document.activeElement);
        if (!bar?.matches(':hover') && !typingInside) {
          setVisible(false);
          closeDropdowns();
        }
      }, 150);
    };
    editor.on('blur', handleBlur);
    return () => {
      editor.off('blur', handleBlur);
    };
  }, [editor, closeDropdowns]);

  // Close dropdowns on outside click
  useEffect(() => {
    if (!visible) return;
    const handleClick = (e: MouseEvent) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
        closeDropdowns();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [visible, closeDropdowns]);

  // Link handler
  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('הכנס כתובת URL:', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url, target: '_blank' })
      .run();
  }, [editor]);

  // Gibberish convert handler
  const handleGibberishConvert = useCallback(() => {
    if (!gibberishConverter) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    if (!selectedText.trim()) return;

    // Detect direction: count Hebrew vs English chars
    let heCount = 0;
    let enCount = 0;
    for (const ch of selectedText) {
      const code = ch.codePointAt(0) ?? 0;
      if (code >= 0x0590 && code <= 0x05ff) heCount++;
      else if ((code >= 0x41 && code <= 0x5a) || (code >= 0x61 && code <= 0x7a)) enCount++;
    }

    const converted = heCount > enCount ? gibberishConverter.toEnglish(selectedText) : gibberishConverter.toHebrew(selectedText);
    if (converted === selectedText) return;

    editor.chain().focus().insertContent(converted).run();
  }, [editor, gibberishConverter]);

  /**
   * Format painter, two clicks. The first click copies the formatting marks
   * sitting on the selection and arms the brush; the second click strips the
   * new selection's marks and stamps the copied ones onto it. Clicking the
   * armed brush with nothing else selected disarms it.
   */
  const handlePainter = useCallback(() => {
    if (painted) {
      const { from, to } = editor.state.selection;
      if (from === to) {
        setPainted(null);
        return;
      }
      let chain = editor.chain().focus().unsetAllMarks();
      for (const mark of painted) {
        chain = chain.setMark(mark.name, mark.attrs);
      }
      chain.run();
      setPainted(null);
      return;
    }

    const { from, to } = editor.state.selection;
    if (from === to) return;
    // Resolve one position INTO the selection: marks at `from` itself are the
    // marks of the character before it, which is not what the user picked.
    const marks = editor.state.doc.resolve(Math.min(from + 1, to)).marks();
    const copied = marks
      .filter((mark) => PAINTABLE_MARKS.includes(mark.type.name))
      .map((mark) => ({ name: mark.type.name, attrs: { ...mark.attrs } }));
    setPainted(copied);
  }, [editor, painted]);

  /**
   * Hands the selected text to the host app's AI callback and swaps the
   * selection for whatever comes back. The package owns no endpoint of its
   * own — without the callback the button is not rendered at all.
   */
  const handleAiAction = useCallback(
    async (kind: AiTextActionKind) => {
      if (!aiTextAction || aiBusy) return;
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, ' ');
      if (!selectedText.trim()) return;

      setShowAi(false);
      setAiBusy(true);
      try {
        const result = await aiTextAction(kind, selectedText);
        if (result && result.trim() && result !== selectedText) {
          editor.chain().focus().insertContentAt({ from, to }, result.trim()).run();
        }
      } catch (err) {
        // The text itself is left exactly as it was — that is the safe
        // outcome. But a silent failure reads as a dead button, so the
        // toolbar says so itself and clears the message after a moment.
        setAiError(err instanceof Error && err.message ? err.message : 'הפעולה נכשלה');
      } finally {
        setAiBusy(false);
      }
    },
    [editor, aiTextAction, aiBusy],
  );

  // The error chip is a message, not a state: it clears itself so the
  // toolbar never carries a stale failure into the next selection.
  useEffect(() => {
    if (!aiError) return;
    const t = setTimeout(() => setAiError(null), 5000);
    return () => clearTimeout(t);
  }, [aiError]);

  /**
   * "הפוך למשימה (vTask)" — creates a new task record from the selected text
   * via the host's onCreateFromText (same createNote/TASK_ENTITY_TYPE path
   * every other task entry point uses — Rule 10). On success, the selected
   * text is REPLACED in place by an ObjectRef chip (blue, clickable,
   * navigates via onNavigateRef — the same node the object-reference field
   * already uses, reused per Rule 10) — the selection becomes the link,
   * it doesn't sit duplicated next to it. (Corrected 23.09 — Gal caught the
   * duplicate-text bug the first version shipped with: "אמרנו להדגיש את
   * הטקסט הקיים", not insert a second copy after it.)
   */
  const handleCreateTask = useCallback(async () => {
    if (!onCreateFromText || taskBusy) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ').trim();
    if (!selectedText) return;

    setShowMore(false);
    setTaskBusy(true);
    setTaskMsg(null);
    try {
      const created = await onCreateFromText('task', selectedText);
      if (created) {
        setTaskMsg(`נוצרה משימה: ${created.title}`);
        // Selection is still [from, to] over the original text — setObjectRef
        // (insertContent under the hood) replaces exactly that range with the
        // chip node, so the source text becomes the link instead of sitting
        // beside a second copy of itself.
        editor
          .chain()
          .focus()
          .setTextSelection({ from, to })
          .command(({ commands }) =>
            commands.setObjectRef({ id: created.id, entityType: 'task', title: created.title })
          )
          .run();
      } else {
        setTaskMsg('יצירת המשימה נכשלה');
      }
    } catch (err) {
      setTaskMsg(err instanceof Error && err.message ? err.message : 'יצירת המשימה נכשלה');
    } finally {
      setTaskBusy(false);
    }
  }, [editor, onCreateFromText, taskBusy]);

  // Same self-clearing pattern as aiError — a one-line confirmation, not a
  // state the toolbar has to carry into the next selection.
  useEffect(() => {
    if (!taskMsg) return;
    const t = setTimeout(() => setTaskMsg(null), 4000);
    return () => clearTimeout(t);
  }, [taskMsg]);

  // ─── Keep the toolbar and its open menu inside the viewport ───
  //
  // Gal, 2026-09-07: «התפריט יוצא מהמסגרת, אתה חייב להוסיף גבולות בשביל
  // שאני אראה את כל חלונית הערכים והאפשרויות». Two separate overflows were
  // happening: the toolbar itself is centred on the selection, so a selection
  // near an edge pushed half of it off screen; and every dropdown opens
  // downwards from the toolbar, so a selection low on the page opened a menu
  // whose bottom rows were below the fold.
  //
  // Both are fixed by measuring after layout and nudging with the `translate`
  // property — deliberately not `transform`, which the toolbar already uses
  // for its own centring and each dropdown uses for its anchoring.
  const anyMenuOpen =
    colorMenu !== null || showAlign || showMore || showFonts || showSizes || showAi;

  const clampIntoView = useCallback(() => {
    const bar = toolbarRef.current;
    if (!bar) return;

    const MARGIN = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // 1. The toolbar. Clamp horizontally only: the vertical flip above the
    //    selection is already decided in updatePosition.
    bar.style.translate = '';
    const barRect = bar.getBoundingClientRect();
    let barDx = 0;
    if (barRect.right > vw - MARGIN) barDx = vw - MARGIN - barRect.right;
    if (barRect.left + barDx < MARGIN) barDx = MARGIN - barRect.left;
    // Vertical safety net measured on the real element: updatePosition works
    // off an approximate height, and a toolbar that wrapped to two rows is
    // taller than that guess.
    let barDy = 0;
    if (barRect.bottom > vh - MARGIN) barDy = vh - MARGIN - barRect.bottom;
    if (barRect.top + barDy < MARGIN) barDy = MARGIN - barRect.top;
    if (barDx !== 0 || barDy !== 0) bar.style.translate = `${barDx}px ${barDy}px`;

    // 2. The open menu, measured against the toolbar's final place.
    const menu = bar.querySelector<HTMLElement>('.gam-ftb__dropdown, .gam-color-menu');
    if (!menu) return;

    menu.style.translate = '';
    menu.style.maxHeight = '';
    menu.style.overflowY = '';

    const barNow = bar.getBoundingClientRect();
    const r = menu.getBoundingClientRect();

    let dx = 0;
    if (r.right > vw - MARGIN) dx = vw - MARGIN - r.right;
    if (r.left + dx < MARGIN) dx = MARGIN - r.left;

    let dy = 0;
    if (r.bottom > vh - MARGIN) {
      // Flip above the toolbar when the menu fits there whole; otherwise keep
      // it below and let it scroll, which is still better than a cut-off list.
      const flippedTop = barNow.top - 6 - r.height;
      if (flippedTop >= MARGIN) {
        dy = -(r.height + barNow.height + 12);
      } else {
        menu.style.maxHeight = `${Math.max(120, vh - r.top - MARGIN)}px`;
        menu.style.overflowY = 'auto';
      }
    }

    if (dx !== 0 || dy !== 0) menu.style.translate = `${dx}px ${dy}px`;
  }, []);

  // Gal, 2026-09-07: «לא רואים את המילה שאני מגדיל». When the selection sits
  // low on the page there is no room under the toolbar, so the menu flips
  // upwards — straight over the text being edited, which is the one thing the
  // user is watching while stepping a number.
  //
  // The fix is not to move the menu but to move the page: scroll the content
  // up just enough for the menu to fit *below* the toolbar, where it covers
  // the lines after the selection instead of the selection itself. The scroll
  // moves the selection too, so it never scrolls past the top edge, and it
  // runs once per opening rather than chasing its own repositioning.
  const menuScrolledRef = useRef(false);
  const editorPadRef = useRef<string | null>(null);

  useEffect(() => {
    const dom = editor.view.dom as HTMLElement;

    const restorePadding = () => {
      if (editorPadRef.current === null) return;
      dom.style.paddingBottom = editorPadRef.current;
      editorPadRef.current = null;
    };

    if (!anyMenuOpen) {
      menuScrolledRef.current = false;
      restorePadding();
      return;
    }
    if (menuScrolledRef.current) return;

    const bar = toolbarRef.current;
    if (!bar) return;
    const menu = bar.querySelector<HTMLElement>('.gam-ftb__dropdown, .gam-color-menu');
    if (!menu) return;

    const MARGIN = 8;
    const barRect = bar.getBoundingClientRect();
    const overflow = barRect.bottom + menu.offsetHeight + MARGIN - window.innerHeight;
    if (overflow <= 0) return; // Already fits below — nothing covers the text.

    // How far the page may move before the selection itself leaves the screen.
    let selTop = barRect.top;
    try {
      const { from, to } = editor.state.selection;
      const a = editor.view.coordsAtPos(from);
      const b = editor.view.coordsAtPos(to);
      selTop = Math.min(a.top, b.top);
    } catch {
      // coordsAtPos throws on a position the view no longer maps; the toolbar
      // top is a safe stand-in, it is never above the selection.
    }
    const delta = Math.min(overflow, selTop - MARGIN);
    if (delta <= 0) return;

    menuScrolledRef.current = true;

    let scroller: HTMLElement = (document.scrollingElement as HTMLElement) ?? document.documentElement;
    let node: HTMLElement | null = dom.parentElement;
    while (node) {
      const oy = window.getComputedStyle(node).overflowY;
      if (/(auto|scroll|overlay)/.test(oy) && node.scrollHeight > node.clientHeight) {
        scroller = node;
        break;
      }
      node = node.parentElement;
    }

    // At the end of the document there is nothing left to scroll into, which is
    // exactly where the menu covered the text. Borrow the missing distance as
    // temporary padding under the last line and give it back on close.
    const room = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop;
    const missing = delta - room;
    if (missing > 0) {
      editorPadRef.current = dom.style.paddingBottom;
      const current = parseFloat(window.getComputedStyle(dom).paddingBottom) || 0;
      dom.style.paddingBottom = `${current + missing}px`;
    }

    scroller.scrollTop += delta;
    // A programmatic scroll does not always reach the scroll listener that
    // keeps the toolbar with its selection, and the bar was left behind at the
    // old height. Setting scrollTop updates layout synchronously, so the new
    // coordinates are readable right here — no frame to wait for.
    updatePosition();
  }, [anyMenuOpen, editor, updatePosition]);

  // The toolbar renders through a portal whose container is created in an
  // effect, so on its first appearance the element does not exist yet when the
  // layout effect below runs — which is exactly why the bar used to sit off
  // screen until something else moved it. A callback ref runs at the moment
  // the element actually mounts, so the first clamp measures a real element.
  const attachToolbar = useCallback((node: HTMLDivElement | null) => {
    toolbarRef.current = node;
    toolbarObserverRef.current?.disconnect();
    toolbarObserverRef.current = null;
    if (!node) return;
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(clampIntoView);
      ro.observe(node);
      toolbarObserverRef.current = ro;
    }
    clampIntoView();
    requestAnimationFrame(clampIntoView);
  }, [clampIntoView]);

  useLayoutEffect(() => {
    if (!visible) return;
    clampIntoView();

    // Gal, 2026-09-07, after the first fix landed: the bar was still sitting
    // 164px past the right edge on a fresh page load, and only jumped into
    // place once something moved. The reason is that the pass above measures
    // the toolbar on its very first paint, before fonts and icons have settled,
    // so the numbers it reads are not the numbers the user ends up seeing.
    // A second pass on the next frame measures the real element, and a
    // ResizeObserver catches every later width change — a live number growing
    // a digit, a menu opening — without waiting for `position` to change.
    const raf = requestAnimationFrame(clampIntoView);
    window.addEventListener('resize', clampIntoView);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', clampIntoView);
    };
  }, [visible, position, flipAbove, anyMenuOpen, colorMenu, showAlign, showMore, showFonts, showSizes, showAi, clampIntoView]);

  // ─── Live numbers for size and line spacing ───
  //
  // Gal, 2026-09-07: «הייתי שמח לשנות את האייקון במספר חי עבור גודל הגופן ...
  // ומוסיף פלוס ומינוס מהצדדים בשביל לקבוע את הגודל בהתאם למספר ולא רק לפי
  // הגדלים שציינת בתפריט». The presets stay — they are the fast path — and
  // the number is the exact one.
  const textStyleAttrs = editor.getAttributes('textStyle');

  // When nothing is set on the selection the honest number is the editor's
  // own computed size, not a hardcoded 16 that may not be true here.
  const baseFontPx = (() => {
    try {
      const px = parseFloat(window.getComputedStyle(editor.view.dom).fontSize);
      return Number.isFinite(px) ? Math.round(px) : 16;
    } catch {
      return 16;
    }
  })();

  const activeFontPx = (() => {
    const raw = textStyleAttrs.fontSize;
    const px = typeof raw === 'string' ? parseFloat(raw) : NaN;
    return Number.isFinite(px) ? Math.round(px) : baseFontPx;
  })();

  const activeLineHeight = (() => {
    const raw = textStyleAttrs.lineHeight;
    const n = typeof raw === 'string' ? parseFloat(raw) : NaN;
    return Number.isFinite(n) ? Math.round(n * 10) / 10 : DEFAULT_LINE_HEIGHT;
  })();

  const applyFontPx = (px: number) => {
    const v = Math.min(FONT_PX_MAX, Math.max(FONT_PX_MIN, Math.round(px)));
    editor.chain().focus().setFontSize(`${v}px`).run();
  };

  const applyLineHeight = (n: number) => {
    const v = Math.min(LINE_HEIGHT_MAX, Math.max(LINE_HEIGHT_MIN, Math.round(n * 10) / 10));
    editor.chain().focus().setLineHeight(String(v)).run();
  };

  // Vertical placement is a cell property, so it is only offered where a cell
  // exists — a paragraph has no height to sit inside.
  const inTableCell = editor.isActive('tableCell') || editor.isActive('tableHeader');
  const cellVerticalAlign =
    (editor.getAttributes(editor.isActive('tableHeader') ? 'tableHeader' : 'tableCell')
      .verticalAlign as string | null) ?? 'top';

  const applyPlacement = (h: 'right' | 'center' | 'left', v: 'top' | 'middle' | 'bottom') => {
    editor.chain().focus().setTextAlign(h).run();
    if (inTableCell) {
      const type = editor.isActive('tableHeader') ? 'tableHeader' : 'tableCell';
      editor.chain().focus().updateAttributes(type, { verticalAlign: v }).run();
    }
  };

  // Get current alignment icon element
  const getAlignIconElement = () => {
    if (editor.isActive({ textAlign: 'center' })) return <AlignCenter size={ICON_SIZE} />;
    if (editor.isActive({ textAlign: 'left' })) return <AlignLeft size={ICON_SIZE} />;
    return <AlignRight size={ICON_SIZE} />;
  };

  // Get current text color for the Type icon underline
  const currentTextColor = editor.getAttributes('textStyle').color || 'var(--cc-accent-400, #a78bfa)';

  if (!visible) return null;

  return (
    <EditorOverlay>
    <div
      ref={attachToolbar}
      className={`gam-ftb ${flipAbove ? 'gam-ftb--above' : 'gam-ftb--below'}`}
      dir="rtl"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translateX(-50%)',
        zIndex: EDITOR_OVERLAY_Z,
      }}
    >
      {/* ─── Formatting Group ─── (family: format — DnD, editMode) */}
      <span
        className={`gam-ftb__family ${editMode ? 'gam-ftb__family--editing' : ''}`}
        style={{ order: famOrder(mainOrder, 'format') }}
        {...familyDragProps('main', 'format', mainOrder.moveFamily)}
      >
        {editMode && <GripVertical size={11} className="gam-ftb__drag-handle" />}
        <span
          className={editMode ? 'gam-ftb__item--editable' : undefined}
          style={{ order: itemOrder(mainOrder, 'format', 'bold') }}
          {...itemDragProps('main', 'format', 'bold', mainOrder.moveItem)}
        >
          <ToolbarBtn
            icon={<Bold size={ICON_SIZE} />}
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Bold (Ctrl+B)"
          />
        </span>
        <span
          className={editMode ? 'gam-ftb__item--editable' : undefined}
          style={{ order: itemOrder(mainOrder, 'format', 'italic') }}
          {...itemDragProps('main', 'format', 'italic', mainOrder.moveItem)}
        >
          <ToolbarBtn
            icon={<Italic size={ICON_SIZE} />}
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Italic (Ctrl+I)"
          />
        </span>
        <span
          className={editMode ? 'gam-ftb__item--editable' : undefined}
          style={{ order: itemOrder(mainOrder, 'format', 'underline') }}
          {...itemDragProps('main', 'format', 'underline', mainOrder.moveItem)}
        >
          <ToolbarBtn
            icon={<Underline size={ICON_SIZE} />}
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            title="Underline (Ctrl+U)"
          />
        </span>
        <span
          className={editMode ? 'gam-ftb__item--editable' : undefined}
          style={{ order: itemOrder(mainOrder, 'format', 'strike') }}
          {...itemDragProps('main', 'format', 'strike', mainOrder.moveItem)}
        >
          <ToolbarBtn
            icon={<Strikethrough size={ICON_SIZE} />}
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            title="Strikethrough"
          />
        </span>
      </span>

      <span className="gam-ftb__sep" style={{ order: famOrder(mainOrder, 'format') + 1 }} />

      {/* ─── Color Group ─── (family: color — DnD, editMode) */}
      <span
        className={`gam-ftb__family ${editMode ? 'gam-ftb__family--editing' : ''}`}
        style={{ order: famOrder(mainOrder, 'color') }}
        {...familyDragProps('main', 'color', mainOrder.moveFamily)}
      >
      {editMode && <GripVertical size={11} className="gam-ftb__drag-handle" />}
      <span
        className={`gam-ftb__color-wrap ${editMode ? 'gam-ftb__item--editable' : ''}`}
        style={{ order: itemOrder(mainOrder, 'color', 'textColor') }}
        {...itemDragProps('main', 'color', 'textColor', mainOrder.moveItem)}
      >
        <ToolbarBtn
          icon={
            <span className="gam-ftb__color-icon">
              <Type size={ICON_SIZE} />
              <span className="gam-ftb__color-bar" style={{ backgroundColor: currentTextColor }} />
            </span>
          }
          active={colorMenu === 'text'}
          onClick={() => {
            setColorMenu(colorMenu === 'text' ? null : 'text');
            setShowAlign(false);
            setShowMore(false);
            setShowFonts(false);
          }}
          title="צבע טקסט"
          hasDropdown
        />
        {colorMenu === 'text' && (
          <ColorMenu editor={editor} type="text" onClose={() => setColorMenu(null)} />
        )}
      </span>

      <span
        className={`gam-ftb__color-wrap ${editMode ? 'gam-ftb__item--editable' : ''}`}
        style={{ order: itemOrder(mainOrder, 'color', 'highlight') }}
        {...itemDragProps('main', 'color', 'highlight', mainOrder.moveItem)}
      >
        <ToolbarBtn
          icon={<Highlighter size={ICON_SIZE} />}
          active={colorMenu === 'background'}
          onClick={() => {
            setColorMenu(colorMenu === 'background' ? null : 'background');
            setShowAlign(false);
            setShowMore(false);
            setShowFonts(false);
          }}
          title="צבע רקע"
          hasDropdown
        />
        {colorMenu === 'background' && (
          <ColorMenu editor={editor} type="background" onClose={() => setColorMenu(null)} />
        )}
      </span>

      {/* ─── Font family ─── */}
      <span
        className={`gam-ftb__color-wrap ${editMode ? 'gam-ftb__item--editable' : ''}`}
        style={{ order: itemOrder(mainOrder, 'color', 'font') }}
        {...itemDragProps('main', 'color', 'font', mainOrder.moveItem)}
      >
        <ToolbarBtn
          icon={<CaseSensitive size={ICON_SIZE} />}
          active={showFonts}
          onClick={() => {
            setShowFonts(!showFonts);
            setColorMenu(null);
            setShowAlign(false);
            setShowMore(false);
          }}
          title="גופן"
          hasDropdown
        />
        {showFonts && (
          <div className="gam-ftb__dropdown gam-ftb__dropdown--fonts">
            <button
              type="button"
              className="gam-ftb__dropdown-item"
              onClick={() => { editor.chain().focus().unsetFontFamily().run(); setShowFonts(false); }}
            >
              <span>ברירת מחדל</span>
            </button>
            {EDITOR_FONT_GROUPS.map((group) => (
              <div key={group.label}>
                <div className="gam-ftb__dropdown-sep" />
                <div className="gam-ftb__dropdown-label">{group.label}</div>
                {group.fonts.map((font) => (
                  <button
                    key={font.id}
                    type="button"
                    className={`gam-ftb__dropdown-item ${
                      editor.isActive('textStyle', { fontFamily: font.stack })
                        ? 'gam-ftb__dropdown-item--active'
                        : ''
                    }`}
                    // The row is set in the font it applies, so the reader sees
                    // the typeface rather than reading its name.
                    style={{ fontFamily: font.stack }}
                    onClick={() => {
                      editor.chain().focus().setFontFamily(font.stack).run();
                      setShowFonts(false);
                    }}
                  >
                    <span>{font.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </span>

      {/* ─── Font size + line height ─── */}
      <span
        className={`gam-ftb__color-wrap ${editMode ? 'gam-ftb__item--editable' : ''}`}
        style={{ order: itemOrder(mainOrder, 'color', 'size') }}
        {...itemDragProps('main', 'color', 'size', mainOrder.moveItem)}
      >
        <ToolbarBtn
          icon={<span className="gam-ftb__num">{activeFontPx}</span>}
          active={showSizes}
          onClick={() => {
            setShowSizes(!showSizes);
            setColorMenu(null);
            setShowAlign(false);
            setShowMore(false);
            setShowFonts(false);
            setShowAi(false);
          }}
          title="גודל גופן וריווח שורות"
          hasDropdown
        />
        {showSizes && (
          <div className="gam-ftb__dropdown">
            <div className="gam-ftb__stepper">
              <span className="gam-ftb__stepper-label">גודל גופן</span>
              <span className="gam-ftb__stepper-controls">
                <button
                  type="button"
                  className="gam-ftb__step-btn"
                  onClick={() => applyFontPx(activeFontPx - 1)}
                  title="הקטן"
                >
                  <Minus size={12} />
                </button>
                <input
                  type="number"
                  className="gam-ftb__step-input"
                  value={activeFontPx}
                  min={FONT_PX_MIN}
                  max={FONT_PX_MAX}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n) && n > 0) applyFontPx(n);
                  }}
                  aria-label="גודל גופן במספר"
                />
                <button
                  type="button"
                  className="gam-ftb__step-btn"
                  onClick={() => applyFontPx(activeFontPx + 1)}
                  title="הגדל"
                >
                  <Plus size={12} />
                </button>
              </span>
            </div>
            {FONT_SIZES.map((size) => (
              <button
                key={size.value}
                type="button"
                className={`gam-ftb__dropdown-item ${
                  editor.isActive('textStyle', { fontSize: size.value })
                    ? 'gam-ftb__dropdown-item--active'
                    : ''
                }`}
                // Each row is set at the size it applies, so the menu shows the
                // scale instead of describing it.
                style={{ fontSize: size.value, lineHeight: 1.3 }}
                onClick={() => {
                  editor.chain().focus().setFontSize(size.value).run();
                  setShowSizes(false);
                }}
              >
                <span>{size.label}</span>
              </button>
            ))}

            <div className="gam-ftb__dropdown-sep" />
            <div className="gam-ftb__stepper">
              <span className="gam-ftb__stepper-label">ריווח שורות</span>
              <span className="gam-ftb__stepper-controls">
                <button
                  type="button"
                  className="gam-ftb__step-btn"
                  onClick={() => applyLineHeight(activeLineHeight - LINE_HEIGHT_STEP)}
                  title="צמצם"
                >
                  <Minus size={12} />
                </button>
                <input
                  type="number"
                  className="gam-ftb__step-input"
                  value={activeLineHeight}
                  step={LINE_HEIGHT_STEP}
                  min={LINE_HEIGHT_MIN}
                  max={LINE_HEIGHT_MAX}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n) && n > 0) applyLineHeight(n);
                  }}
                  aria-label="ריווח שורות במספר"
                />
                <button
                  type="button"
                  className="gam-ftb__step-btn"
                  onClick={() => applyLineHeight(activeLineHeight + LINE_HEIGHT_STEP)}
                  title="הרחב"
                >
                  <Plus size={12} />
                </button>
              </span>
            </div>
            {LINE_HEIGHTS.map((lh) => (
              <button
                key={lh.value}
                type="button"
                className={`gam-ftb__dropdown-item ${
                  editor.isActive('textStyle', { lineHeight: lh.value })
                    ? 'gam-ftb__dropdown-item--active'
                    : ''
                }`}
                onClick={() => {
                  editor.chain().focus().setLineHeight(lh.value).run();
                  setShowSizes(false);
                }}
              >
                <span>{lh.label}</span>
              </button>
            ))}

            <div className="gam-ftb__dropdown-sep" />
            <button
              type="button"
              className="gam-ftb__dropdown-item"
              onClick={() => {
                editor.chain().focus().unsetFontSize().unsetLineHeight().run();
                setShowSizes(false);
              }}
            >
              <RemoveFormatting size={14} />
              <span>ברירת מחדל</span>
            </button>
          </div>
        )}
      </span>
      </span>

      <span className="gam-ftb__sep" style={{ order: famOrder(mainOrder, 'color') + 1 }} />

      {/* ─── Link ─── (family: link) */}
      <span
        className={`gam-ftb__family ${editMode ? 'gam-ftb__family--editing' : ''}`}
        style={{ order: famOrder(mainOrder, 'link') }}
        {...familyDragProps('main', 'link', mainOrder.moveFamily)}
      >
        {editMode && <GripVertical size={11} className="gam-ftb__drag-handle" />}
        <ToolbarBtn
          icon={<Link size={ICON_SIZE} />}
          active={editor.isActive('link')}
          onClick={setLink}
          title="קישור"
        />
      </span>
      <span className="gam-ftb__sep" style={{ order: famOrder(mainOrder, 'link') + 1 }} />

      {/* ─── Alignment ─── (family: align) */}
      <span
        className={`gam-ftb__family ${editMode ? 'gam-ftb__family--editing' : ''}`}
        style={{ order: famOrder(mainOrder, 'align') }}
        {...familyDragProps('main', 'align', mainOrder.moveFamily)}
      >
      {editMode && <GripVertical size={11} className="gam-ftb__drag-handle" />}
      <span className="gam-ftb__color-wrap">
        <ToolbarBtn
          icon={getAlignIconElement()}
          active={showAlign}
          onClick={() => {
            setShowAlign(!showAlign);
            setColorMenu(null);
            setShowMore(false);
            setShowFonts(false);
          }}
          title="מיקום הטקסט"
          hasDropdown
        />
        {showAlign && (
          <div className="gam-ftb__dropdown gam-ftb__dropdown--place">
            <div className="gam-ftb__dropdown-label">מיקום התוכן</div>
            <div className="gam-ftb__place-grid" dir="rtl">
              {/* Vertical placement only means something inside a box that has a
                 height. Outside a table cell we render the horizontal row alone
                 rather than six controls that would silently do nothing. */}
              {(inTableCell ? PLACEMENTS : PLACEMENTS.filter(([v]) => v === 'middle')).map(([v, h]) => {
                const key = `${v}-${h}`;
                const label = inTableCell ? PLACEMENT_LABELS[key] : HORIZONTAL_LABELS[h];
                const isActive =
                  editor.isActive({ textAlign: h }) &&
                  (!inTableCell || cellVerticalAlign === v);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`gam-ftb__place-cell ${isActive ? 'gam-ftb__place-cell--active' : ''}`}
                    title={label}
                    aria-label={label}
                    onClick={() => {
                      applyPlacement(h, v);
                      setShowAlign(false);
                    }}
                  >
                    <span className={`gam-ftb__place-dot gam-ftb__place-dot--${v} gam-ftb__place-dot--${h}`} />
                  </button>
                );
              })}
            </div>
            {!inTableCell && (
              <div className="gam-ftb__place-note">
                מיקום לגובה נפתח בתוך תא בטבלה
              </div>
            )}
          </div>
        )}
      </span>
      </span>

      <span className="gam-ftb__sep" style={{ order: famOrder(mainOrder, 'align') + 1 }} />

      {/* ─── Format painter ─── (family: painter) */}
      <span
        className={`gam-ftb__family ${editMode ? 'gam-ftb__family--editing' : ''}`}
        style={{ order: famOrder(mainOrder, 'painter') }}
        {...familyDragProps('main', 'painter', mainOrder.moveFamily)}
      >
        {editMode && <GripVertical size={11} className="gam-ftb__drag-handle" />}
        <ToolbarBtn
          icon={<Paintbrush size={ICON_SIZE} />}
          active={painted !== null}
          onClick={handlePainter}
          title={painted ? 'סמן טקסט אחר ולחץ שוב כדי להחיל את העיצוב' : 'העתק עיצוב'}
        />
      </span>

      {/* ─── AI on the selection ─── (family: ai, conditional) */}
      {aiTextAction && (
        <span
          className={`gam-ftb__family ${editMode ? 'gam-ftb__family--editing' : ''}`}
          style={{ order: famOrder(mainOrder, 'ai') }}
          {...familyDragProps('main', 'ai', mainOrder.moveFamily)}
        >
        {editMode && <GripVertical size={11} className="gam-ftb__drag-handle" />}
        <span className="gam-ftb__color-wrap">
          <ToolbarBtn
            icon={aiBusy ? <Loader2 size={ICON_SIZE} className="gam-ftb__spin" /> : <Sparkles size={ICON_SIZE} />}
            active={showAi}
            onClick={() => {
              if (aiBusy) return;
              setShowAi(!showAi);
              setColorMenu(null);
              setShowAlign(false);
              setShowMore(false);
              setShowFonts(false);
              setShowSizes(false);
            }}
            title="פעולת AI על הטקסט המסומן"
            hasDropdown
          />
          {showAi && !aiBusy && (
            <div className="gam-ftb__dropdown">
              {AI_ACTIONS.map((action) => (
                <button
                  key={action.kind}
                  type="button"
                  className="gam-ftb__dropdown-item"
                  onClick={() => { void handleAiAction(action.kind); }}
                >
                  <Sparkles size={14} />
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          )}
        </span>
        </span>
      )}

      {/* ─── Gibberish Convert ─── (family: lang, conditional) */}
      {gibberishConverter && (
        <span
          className={`gam-ftb__family ${editMode ? 'gam-ftb__family--editing' : ''}`}
          style={{ order: famOrder(mainOrder, 'lang') }}
          {...familyDragProps('main', 'lang', mainOrder.moveFamily)}
        >
          {editMode && <GripVertical size={11} className="gam-ftb__drag-handle" />}
          <ToolbarBtn
            icon={<Languages size={ICON_SIZE} />}
            active={false}
            onClick={handleGibberishConvert}
            title="תיקון שפה"
          />
        </span>
      )}

      <span className="gam-ftb__sep" style={{ order: 9990 }} />
      {/* ─── Edit-mode toggle — pinned, always last before "…" ─── */}
      <ToolbarBtn
        icon={<GripVertical size={ICON_SIZE} />}
        active={editMode}
        onClick={() => setEditMode((v) => !v)}
        title={editMode ? 'סיים סידור סרגל הכלים' : 'סדר מחדש את סרגל הכלים (גרור)'}
      />

      {/* ─── More Menu ─── (pinned last) */}
      <span className="gam-ftb__color-wrap" style={{ order: 10000 }}>
        <ToolbarBtn
          icon={<MoreHorizontal size={ICON_SIZE} />}
          active={showMore}
          onClick={() => {
            setShowMore(!showMore);
            setColorMenu(null);
            setShowAlign(false);
            setShowFonts(false);
          }}
          title="עוד אפשרויות"
        />
        {showMore && (
          <div className="gam-ftb__dropdown gam-ftb__dropdown--more">
            {/* Turn Into — headings family */}
            <div
              className={`gam-ftb__more-family${editMode ? ' gam-ftb__more-family--editing' : ''}`}
              style={{ order: famOrder(moreOrder, 'headings') }}
              {...familyDragProps('more', 'headings', moreOrder.moveFamily)}
            >
              <div className="gam-ftb__dropdown-label">
                {editMode && <GripVertical size={11} className="gam-ftb__drag-handle" />}
                <span>הפוך ל...</span>
              </div>
              <span style={{ order: itemOrder(moreOrder, 'headings', 'h1') }} {...itemDragProps('more', 'headings', 'h1', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().toggleHeading({ level: 1 }).run(); setShowMore(false); }}>
                  <Heading1 size={14} /> <span>כותרת 1</span>
                </button>
              </span>
              <span style={{ order: itemOrder(moreOrder, 'headings', 'h2') }} {...itemDragProps('more', 'headings', 'h2', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().toggleHeading({ level: 2 }).run(); setShowMore(false); }}>
                  <Heading2 size={14} /> <span>כותרת 2</span>
                </button>
              </span>
              <span style={{ order: itemOrder(moreOrder, 'headings', 'h3') }} {...itemDragProps('more', 'headings', 'h3', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().toggleHeading({ level: 3 }).run(); setShowMore(false); }}>
                  <Heading3 size={14} /> <span>כותרת 3</span>
                </button>
              </span>
            </div>

            <div className="gam-ftb__dropdown-sep" style={{ order: famOrder(moreOrder, 'headings') + 1 }} />

            {/* Lists family */}
            <div
              className={`gam-ftb__more-family${editMode ? ' gam-ftb__more-family--editing' : ''}`}
              style={{ order: famOrder(moreOrder, 'lists') }}
              {...familyDragProps('more', 'lists', moreOrder.moveFamily)}
            >
              {editMode && (
                <div className="gam-ftb__more-family-handle">
                  <GripVertical size={11} className="gam-ftb__drag-handle" />
                </div>
              )}
              <span style={{ order: itemOrder(moreOrder, 'lists', 'bulletList') }} {...itemDragProps('more', 'lists', 'bulletList', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().toggleBulletList().run(); setShowMore(false); }}>
                  <List size={14} /> <span>רשימה</span>
                </button>
              </span>
              <span style={{ order: itemOrder(moreOrder, 'lists', 'orderedList') }} {...itemDragProps('more', 'lists', 'orderedList', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().toggleOrderedList().run(); setShowMore(false); }}>
                  <ListOrdered size={14} /> <span>ממוספרת</span>
                </button>
              </span>
              <span style={{ order: itemOrder(moreOrder, 'lists', 'taskList') }} {...itemDragProps('more', 'lists', 'taskList', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().toggleTaskList().run(); setShowMore(false); }}>
                  <CheckSquare size={14} /> <span>משימות</span>
                </button>
              </span>
            </div>

            <div className="gam-ftb__dropdown-sep" style={{ order: famOrder(moreOrder, 'lists') + 1 }} />

            {/* Blocks family */}
            <div
              className={`gam-ftb__more-family${editMode ? ' gam-ftb__more-family--editing' : ''}`}
              style={{ order: famOrder(moreOrder, 'blocks') }}
              {...familyDragProps('more', 'blocks', moreOrder.moveFamily)}
            >
              {editMode && (
                <div className="gam-ftb__more-family-handle">
                  <GripVertical size={11} className="gam-ftb__drag-handle" />
                </div>
              )}
              <span style={{ order: itemOrder(moreOrder, 'blocks', 'quote') }} {...itemDragProps('more', 'blocks', 'quote', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().toggleBlockquote().run(); setShowMore(false); }}>
                  <Quote size={14} /> <span>ציטוט</span>
                </button>
              </span>
              <span style={{ order: itemOrder(moreOrder, 'blocks', 'code') }} {...itemDragProps('more', 'blocks', 'code', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().toggleCodeBlock().run(); setShowMore(false); }}>
                  <Code size={14} /> <span>קוד</span>
                </button>
              </span>
              <span style={{ order: itemOrder(moreOrder, 'blocks', 'divider') }} {...itemDragProps('more', 'blocks', 'divider', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().setHorizontalRule().run(); setShowMore(false); }}>
                  <Minus size={14} /> <span>קו הפרדה</span>
                </button>
              </span>
            </div>

            {/* Text → task record. Reuses the existing createNote/
                TASK_ENTITY_TYPE path (Rule 10) — the selected text becomes a
                real vTask record in the "משימות" tab, and an ObjectRef chip
                is inserted after the selection linking to it. Renamed
                22.09 (Gal's Q2 answer) to disambiguate from the /משימה
                checkbox toggle — this button creates an entity, the slash
                checkbox does not. records = its own single-item family. */}
            {onCreateFromText && (
              <>
                <div className="gam-ftb__dropdown-sep" style={{ order: famOrder(moreOrder, 'blocks') + 1 }} />
                <div
                  className={`gam-ftb__more-family${editMode ? ' gam-ftb__more-family--editing' : ''}`}
                  style={{ order: famOrder(moreOrder, 'records') }}
                  {...familyDragProps('more', 'records', moreOrder.moveFamily)}
                >
                  {editMode && (
                    <div className="gam-ftb__more-family-handle">
                      <GripVertical size={11} className="gam-ftb__drag-handle" />
                    </div>
                  )}
                  <button
                    type="button"
                    className="gam-ftb__dropdown-item"
                    disabled={taskBusy}
                    onClick={() => setTaskKindOpen((v) => !v)}
                  >
                    {taskBusy ? <Loader2 size={14} className="gam-ftb__spin" /> : <ListTodo size={14} />}
                    <span>הפוך למשימה (vTask)</span>
                  </button>
                  {/* Kind picker — vTask is the only wired kind today (Gal,
                      22.09 Q1: "1 הוא התשובה הנכונה אבל אפשר להרחיב אותו").
                      The other four render disabled so the future shape is
                      visible instead of invented silently later. */}
                  {taskKindOpen && (
                    <div style={{ paddingInlineStart: 12 }}>
                      <button
                        type="button"
                        className="gam-ftb__dropdown-item"
                        disabled={taskBusy}
                        onClick={() => { setTaskKindOpen(false); void handleCreateTask(); }}
                      >
                        {taskBusy ? <Loader2 size={14} className="gam-ftb__spin" /> : <ListTodo size={14} />}
                        <span>vTask — משימה</span>
                      </button>
                      {[
                        ['vRule', 'חוק'],
                        ['vTag', 'תגית'],
                        ['vCollection', 'אוסף'],
                        ['Reference', 'הפניה'],
                      ].map(([kind, label]) => (
                        <button key={kind} type="button" className="gam-ftb__dropdown-item" disabled>
                          <ListTodo size={14} style={{ opacity: 0.4 }} />
                          <span style={{ opacity: 0.6 }}>{kind} — {label} (בקרוב)</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="gam-ftb__dropdown-sep" style={{ order: famOrder(moreOrder, 'records') + 1 }} />

            {/* Callouts family — the five variants CalloutBlock already supports. They
                existed as nodes and in the slash menu, but never here, which is
                why "אזהרה" looked missing from the toolbar. */}
            <div
              className={`gam-ftb__more-family${editMode ? ' gam-ftb__more-family--editing' : ''}`}
              style={{ order: famOrder(moreOrder, 'callouts') }}
              {...familyDragProps('more', 'callouts', moreOrder.moveFamily)}
            >
              {editMode && (
                <div className="gam-ftb__more-family-handle">
                  <GripVertical size={11} className="gam-ftb__drag-handle" />
                </div>
              )}
              {CALLOUTS.map(({ variant, icon, label, Glyph }) => {
                const itemId = `callout-${variant}`;
                return (
                  <span
                    key={variant}
                    style={{ order: itemOrder(moreOrder, 'callouts', itemId) }}
                    {...itemDragProps('more', 'callouts', itemId, moreOrder.moveItem)}
                  >
                    <button
                      type="button"
                      className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`}
                      onClick={() => {
                        editor.chain().focus().setCallout({ variant, icon }).run();
                        setShowMore(false);
                      }}
                    >
                      <Glyph size={14} /> <span>{label}</span>
                    </button>
                  </span>
                );
              })}
            </div>

            <div className="gam-ftb__dropdown-sep" style={{ order: famOrder(moreOrder, 'callouts') + 1 }} />

            {/* Actions family */}
            <div
              className={`gam-ftb__more-family${editMode ? ' gam-ftb__more-family--editing' : ''}`}
              style={{ order: famOrder(moreOrder, 'actions') }}
              {...familyDragProps('more', 'actions', moreOrder.moveFamily)}
            >
              {editMode && (
                <div className="gam-ftb__more-family-handle">
                  <GripVertical size={11} className="gam-ftb__drag-handle" />
                </div>
              )}
              <span style={{ order: itemOrder(moreOrder, 'actions', 'clearFormat') }} {...itemDragProps('more', 'actions', 'clearFormat', moreOrder.moveItem)}>
                <button type="button" className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`} onClick={() => { editor.chain().focus().clearNodes().unsetAllMarks().run(); setShowMore(false); }}>
                  <RemoveFormatting size={14} /> <span>נקה עיצוב</span>
                </button>
              </span>
              <span style={{ order: itemOrder(moreOrder, 'actions', 'copyBlock') }} {...itemDragProps('more', 'actions', 'copyBlock', moreOrder.moveItem)}>
                <button
                  type="button"
                  className={`gam-ftb__dropdown-item${editMode ? ' gam-ftb__item--editable' : ''}`}
                  onClick={() => {
                    const { from, to } = editor.state.selection;
                    const text = editor.state.doc.textBetween(from, to, '\n');
                    navigator.clipboard.writeText(text);
                    setShowMore(false);
                  }}
                >
                  <Copy size={14} /> <span>העתק בלוק</span>
                </button>
              </span>
              <span style={{ order: itemOrder(moreOrder, 'actions', 'deleteBlock') }} {...itemDragProps('more', 'actions', 'deleteBlock', moreOrder.moveItem)}>
                <button
                  type="button"
                  className={`gam-ftb__dropdown-item gam-ftb__dropdown-item--danger${editMode ? ' gam-ftb__item--editable' : ''}`}
                  onClick={() => {
                    editor.chain().focus().deleteSelection().run();
                    setShowMore(false);
                  }}
                >
                  <Trash2 size={14} /> <span>מחק בלוק</span>
                </button>
              </span>
            </div>
          </div>
        )}
      </span>

      {/* A failed AI action must say so — otherwise the button reads as dead. */}
      {aiError && (
        <div className="gam-ftb__error" role="status">
          {aiError}
        </div>
      )}
      {taskMsg && (
        <div className="gam-ftb__error" role="status">
          {taskMsg}
        </div>
      )}
    </div>
    </EditorOverlay>
  );
}

// ─── Toolbar Button Component ──────────────────────
function ToolbarBtn({
  icon,
  active,
  onClick,
  title,
  hasDropdown,
}: {
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  title: string;
  hasDropdown?: boolean;
}) {
  return (
    <button
      type="button"
      className={`gam-ftb__btn ${active ? 'gam-ftb__btn--active' : ''}`}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
    >
      {icon}
      {hasDropdown && <span className="gam-ftb__arrow">▾</span>}
    </button>
  );
}
